<script lang="ts">
	import { format } from 'svelte-i18n';
	//Design system components
	import { Icon, IconButton, AdvancedCardV2 } from '@Shelly/shelly-design-system';
	//Helpers
	import { capFirstChar } from 'src/helpers/utilities';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { getTemplateComponents, getTemplateService } from 'src/helpers/xt1_helpers/template_helpers';
	//Components
	import ThermostatTemperatureWheel from 'src/components/XT1/Thermostat/ThermostatTemperatureWheel.svelte';
	import ThermostatHumidityWheel from 'src/components/XT1/Thermostat/ThermostatHumidityWheel.svelte';
	import ThermostatModeModal from 'src/components/XT1/Thermostat/ThermostatModeModal.svelte';
	//Types
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import type { Card } from 'src/components/SelectList/SelectList.types';
	import type { TemplateComponent, VCInterfaceStatus } from 'src/types/VirtualComponents';
	import { getWorkingModeIcon } from 'src/helpers/device_data_helpers';

	export let device: Device, statuses: VCInterfaceStatus[], mode: string;

	const workingModeRole = 'working_mode';
	const fanSpeedRole = 'fan_speed';
	const fanIconsMapper = {
		auto: 'fas fa-a',
		low: 'fak fa-solid-fan-speed-low',
		medium: 'fak fa-solid-fan-speed-medium',
		high: 'fak fa-solid-fan-speed-high',
	};

	const labelFanSpeed = $format('label_fan_speed', {
		default: `Fan speed`,
	});
	const labelChooseFan = $format('label_fan_choose', {
		default: 'Choose your fan speed',
	});

	const labelThermostatMode = $format('label_thermostat_mode', {
		default: `Thermostat mode`,
	});
	const labelChooseThermostat = $format('label_thermostat_mode_choose', {
		default: 'Choose your thermostat work mode',
	});

	const thermostatModeOptions: Card[] = [
		{
			id: 'auto',
			name: $format('select_thermostat_mode_option_auto', { default: 'Auto' }),
			icon: 'fas fa-a',
			selected: false,
		},
		{
			id: 'cool',
			name: $format('select_thermostat_mode_option_cool', {
				default: 'Cooling',
			}),
			icon: 'fas fa-temperature-snow',
			selected: false,
		},
		{
			id: 'heat',
			name: $format('select_thermostat_mode_option_heating', {
				default: 'Heating',
			}),
			icon: 'fas fa-temperature-sun',
			selected: false,
		},
	];

	let waitForWorkingModesResponse = '';
	let waitForThermostatModeResponse = false;
	let waitForFanModeResponse = false;
	let targetLockWorkingMode = false;
	let fanSpeedLock = false;
	let thermostatModeLock = false;
	let workingModes: TemplateComponent;
	let fanSpeedModes: TemplateComponent;
	let workingModesOptions: string[];
	let fanSpeedOptions: string[];
	let thermostatMode: string;
	let fanSpeedMode: string;
	let showTermModeModal = false;
	let showFanModeModal = false;

	const genFanSpeedOptions = (options: string[]): Card[] => {
		return (options || []).map((i) => {
			return {
				id: i,
				name: $format('label_fan_mode_' + i, {
					default: capFirstChar(i),
				}),
				selected: false,
				icon: fanIconsMapper[i],
			};
		});
	};

	const onSettingChange = (device: Device) => {
		workingModes = getTemplateComponents(device, null, new RegExp(workingModeRole))?.[0];
		const _mode = statuses.find((i) => new RegExp(workingModeRole).test(i.role))?.event;
		fanSpeedModes = getTemplateComponents(device, null, new RegExp(fanSpeedRole))?.[0];
		const _fanSpeedMode = statuses.find((i) => new RegExp(fanSpeedRole).test(i.role))?.event;

		const _options = workingModes?.config?.options || [];
		const _fanSpeedOptions = fanSpeedModes?.config?.options || [];
		const _thermostatMode = getTemplateService(device)?.settings?.thermostat_mode || 'auto';
		if (workingModesOptions != _options) {
			workingModesOptions = _options;
		}
		if (fanSpeedOptions != _fanSpeedOptions) {
			fanSpeedOptions = _fanSpeedOptions;
		}
		if (!waitForWorkingModesResponse && !targetLockWorkingMode && mode !== _mode) {
			mode = _mode;
		}
		if (targetLockWorkingMode && mode == _mode) {
			targetLockWorkingMode = false;
		}
		if (!waitForThermostatModeResponse && !thermostatModeLock && thermostatMode != _thermostatMode) {
			thermostatMode = _thermostatMode;
		}
		if (thermostatModeLock && thermostatMode == _thermostatMode) {
			thermostatModeLock = false;
		}
		if (!waitForFanModeResponse && !fanSpeedLock && fanSpeedMode != _fanSpeedMode) {
			fanSpeedMode = _fanSpeedMode;
		}
		if (fanSpeedLock && fanSpeedMode == _fanSpeedMode) {
			fanSpeedLock = false;
		}
	};

	const saveWorkingModes = async (option: string) => {
		waitForWorkingModesResponse = option;
		const res: SettingRequest = await gen2Request(device, `${workingModes.type}.set`, {
			id: workingModes.config.id,
			value: option,
		});
		if (res?.isok) {
			targetLockWorkingMode = true;
			mode = option;
		} else {
			onSettingsResponse(res, device);
		}
		waitForWorkingModesResponse = '';
	};

	const saveFanSpeedMode = async (option: string) => {
		// Send req only when new value selected
		if (option !== fanSpeedMode) {
			waitForFanModeResponse = true;
			const res: SettingRequest = await gen2Request(device, `${fanSpeedModes.type}.set`, {
				id: fanSpeedModes.config.id,
				value: option,
			});
			if (res?.isok) {
				fanSpeedLock = true;
				fanSpeedMode = option;
			} else {
				onSettingsResponse(res, device);
			}
			waitForFanModeResponse = false;
		}

		onCloseFanModeModal();
	};

	const saveThermostatMode = async (option: string) => {
		// Send req only when new value selected
		if (option !== thermostatMode) {
			waitForThermostatModeResponse = true;
			const config = {
				thermostat_mode: option,
			};
			let res: SettingRequest = await gen2Request(device, 'Service.SetConfig', {
				id: 0,
				config,
			});
			if (res?.isok) {
				thermostatModeLock = true;
				thermostatMode = option;
			} else {
				onSettingsResponse(res, device);
			}
			waitForThermostatModeResponse = false;
		}

		onCloseTermModeModal();
	};

	const getModeTitle = (cardList: Card[], selectedValue: string | number) => {
		return cardList.find((card) => card.id == selectedValue)?.name || cardList[0]?.name;
	};

	const onCloseTermModeModal = () => {
		showTermModeModal = false;
	};

	const onCloseFanModeModal = () => {
		showFanModeModal = false;
	};

	$: onSettingChange(device);
	$: fanModeOptions = genFanSpeedOptions(fanSpeedOptions);
</script>

{#if workingModes && mode != null}
	{#if mode == 'dry'}
		<ThermostatHumidityWheel {device} {statuses} />
	{:else}
		<ThermostatTemperatureWheel {device} {statuses} />
	{/if}
	<div class="d-flex justify-content-center mb-m">
		{#each workingModesOptions as workingMode}
			<IconButton
				aria-label={$format('button_set_mode', {
					default: 'Set mode',
				})}
				class="ml-xs mr-xs"
				style={`${
					mode === workingMode || waitForWorkingModesResponse == workingMode
						? 'pointer-events:none;'
						: ''
				}`}
				loading={waitForWorkingModesResponse == workingMode}
				variant={`${mode === workingMode ? 'primary' : 'secondary'}`}
				icon={getWorkingModeIcon(workingMode)}
				on:click={() => saveWorkingModes(workingMode)}
			/>
		{/each}
	</div>

	<AdvancedCardV2
		class="mb-s"
		aria-label={getModeTitle(thermostatModeOptions, thermostatMode)}
		title={getModeTitle(thermostatModeOptions, thermostatMode)}
		subtitle={{
			value: labelThermostatMode,
			bold: false,
			type: 'muted',
		}}
		avatar={{ icon: 'fal fa-temperature-sun' }}
		isCondensed
		isFocusable
		on:click={() => (showTermModeModal = true)}
	>
		<Icon class="align-self-center" icon="fas fa-chevron-right" size="s" slot="right" />
	</AdvancedCardV2>

	<ThermostatModeModal
		on:save={(e) => {
			saveThermostatMode(e.detail['selectedValue']);
		}}
		on:close={onCloseTermModeModal}
		showModal={showTermModeModal}
		title={labelThermostatMode}
		heading={labelChooseThermostat}
		selectedValue={thermostatMode}
		itemsList={thermostatModeOptions}
		isLoading={waitForThermostatModeResponse}
	/>

	{#if fanModeOptions.length > 0}
		<AdvancedCardV2
			aria-label={getModeTitle(fanModeOptions, fanSpeedMode)}
			title={getModeTitle(fanModeOptions, fanSpeedMode)}
			subtitle={{
				value: labelFanSpeed,
				bold: false,
				type: 'muted',
			}}
			avatar={{ icon: fanIconsMapper[fanSpeedMode] || fanIconsMapper.auto }}
			isCondensed
			isFocusable
			on:click={() => (showFanModeModal = true)}
		>
			<Icon class="align-self-center" icon="fas fa-chevron-right" size="s" slot="right" />
		</AdvancedCardV2>

		<ThermostatModeModal
			on:save={(e) => {
				saveFanSpeedMode(e.detail['selectedValue']);
			}}
			on:close={onCloseFanModeModal}
			showModal={showFanModeModal}
			title={labelFanSpeed}
			heading={labelChooseFan}
			selectedValue={fanSpeedMode}
			itemsList={fanModeOptions}
			isLoading={waitForFanModeResponse}
		/>
	{/if}
{/if}
